import React, { FC, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    DEFAULT_LANG,
    SUPPORTED_LANG_CODES,
    buildLocalizedPath,
} from '../../config/languages';
import { useCurrentLocale } from '../../hooks/useCurrentLocale';
import {
    DEFAULT_OG_IMAGE,
    DEFAULT_OG_IMAGE_ALT,
    SEO_BASE_URL,
    TWITTER_CREATOR,
    TWITTER_SITE,
} from './seoConfig';

export type SeoHeadProps = {
    title: string;
    description: string;
    /** Path without language prefix, e.g. '/', '/trps', '/blog'. */
    basePath: string;
    ogType?: 'website' | 'article';
    ogImage?: string;
    ogImageAlt?: string;
    keywords?: string;
    robots?: string;
    /** Passed to Helmet key — use i18n.language when copy changes per locale. */
    helmetKey?: string;
    children?: ReactNode;
};

/**
 * Shared SEO head for public landing pages: title, description, canonical,
 * hreflang, Open Graph, and Twitter cards.
 */
export const SeoHead: FC<SeoHeadProps> = ({
    title,
    description,
    basePath,
    ogType = 'website',
    ogImage = DEFAULT_OG_IMAGE,
    ogImageAlt = DEFAULT_OG_IMAGE_ALT,
    keywords,
    robots,
    helmetKey,
    children,
}) => {
    const lang = useCurrentLocale();
    const canonicalHref = `${SEO_BASE_URL}${buildLocalizedPath(basePath, lang)}`;
    const defaultHref = `${SEO_BASE_URL}${buildLocalizedPath(basePath, DEFAULT_LANG)}`;

    return (
        <Helmet key={helmetKey}>
            <title>{title}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            {robots && <meta name="robots" content={robots} />}

            <link rel="canonical" href={canonicalHref} />
            {SUPPORTED_LANG_CODES.map((l) => (
                <link
                    key={l}
                    rel="alternate"
                    hrefLang={l}
                    href={`${SEO_BASE_URL}${buildLocalizedPath(basePath, l)}`}
                />
            ))}
            <link rel="alternate" hrefLang="x-default" href={defaultHref} />

            <meta property="og:type" content={ogType} />
            <meta property="og:url" content={canonicalHref} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={ogImageAlt} />
            <meta property="og:site_name" content="BalancePath" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalHref} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={ogImageAlt} />
            <meta name="twitter:site" content={TWITTER_SITE} />
            <meta name="twitter:creator" content={TWITTER_CREATOR} />

            {children}
        </Helmet>
    );
};
